
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { EventBus } from '@/main';
import { PurchaseOrders } from '@/collections/purchase_orders';
import { PurchaseOrderModel, IPurchaseOrderItem } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { contextSyncService } from '@/services/contextSync';
import DeliveryLocationDialog, { LocationSelection } from './DeliveryLocationDialog.vue';

/**
 * Chat response structure for PO display
 */
interface PODisplayChatResponse {
  context?: Record<string, unknown>;
  messages?: Array<{ content: string }>;
}

/**
 * Draft PO data passed directly from backend (no API fetch needed)
 */
interface DraftPOData {
  id: number | null;
  poNumber: string;
  status: string;
  supplierId: number;
  supplierName: string;
  orderDate: string;
  expectedDeliveryDate: string | null;
  locationId?: number;      // Delivery location ID (if already selected)
  locationName?: string;    // Delivery location name
  items: Array<{
    variantId: string;
    productName: string;
    variantName: string;
    sku: string;
    orderedQuantity: number;
    unitPrice: number;
    totalPrice: number;
    variantImage?: string;
  }>;
  totalAmount: number;
  notes: string | null;
  createdAt: string;
}

/**
 * Action parameters for PO display
 * Supports multiple ways to identify a PO:
 * - po_id: numeric ID for direct PO creation from frontend
 * - draft_po_id: numeric ID for agent draft flow (backward compatible)
 * - po_number: PO number string (e.g., "PO-2023-1023") for lookup by number
 * - draft_po: Full draft PO data (for preview, no API fetch needed)
 */
interface PODisplayActionParameters {
  po_id?: string;        // Used for direct PO creation from frontend
  draft_po_id?: string;  // Used for agent draft flow (backward compatible)
  po_number?: string;    // Used when agent passes PO number instead of ID
  draft_po?: DraftPOData; // Used for draft preview from AI Review (no API fetch)
}

@Component({
  name: 'PurchaseOrderDisplay',
  components: {
    DeliveryLocationDialog
  }
})
export default class PurchaseOrderDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: PODisplayChatResponse | null;
  @Prop({ default: (): PODisplayActionParameters => ({}) }) readonly actionParameters!: PODisplayActionParameters;

  private loading = false;
  private error: string | null = null;
  private purchaseOrder: PurchaseOrderModel | null = null;
  private purchaseOrders = new PurchaseOrders();
  private fetchRequestId = 0;
  private showLocationDialog = false;
  private creating = false;

  // Expose CurrencyHelper to template
  private CurrencyHelper = CurrencyHelper;

  private tableHeaders = [
    { text: 'Product', value: 'productName', sortable: false, width: '35%' },
    { text: 'SKU', value: 'sku', sortable: false, width: '15%' },
    { text: 'Quantity', value: 'orderedQuantity', align: 'center', sortable: false, width: '15%' },
    { text: 'Unit Price', value: 'unitPrice', align: 'right', sortable: false, width: '15%' },
    { text: 'Total', value: 'totalPrice', align: 'right', sortable: false, width: '20%' }
  ];

  /**
   * Get PO identifier from action parameters
   * Supports po_id (direct creation), draft_po_id (agent flow), or po_number
   * The backend auto-detects whether it's a numeric ID or PO number string
   */
  get poIdentifier(): string {
    return this.actionParameters?.po_id || this.actionParameters?.draft_po_id || this.actionParameters?.po_number || '';
  }

  /**
   * Check if draft PO data is provided directly (from AI Review)
   */
  get hasDraftPOData(): boolean {
    return !!this.actionParameters?.draft_po;
  }

  /**
   * Check if we have any valid identifier to load a PO
   */
  get hasValidIdentifier(): boolean {
    return !!this.poIdentifier || this.hasDraftPOData;
  }

  /**
   * Check if this is a real PO (created directly, already saved in DB)
   * Real POs use po_id parameter OR po_number parameter
   */
  get isRealPO(): boolean {
    return !!this.actionParameters?.po_id || !!this.actionParameters?.po_number;
  }

  /**
   * Check if this is a draft preview (from agent flow, not yet finalized)
   * Draft previews use draft_po_id parameter OR draft_po data
   */
  get isDraftPreview(): boolean {
    return this.hasDraftPOData ||
      (!!this.actionParameters?.draft_po_id && !this.actionParameters?.po_id && !this.actionParameters?.po_number);
  }

  get poNumber(): string {
    return this.purchaseOrder?.poNumber || 'N/A';
  }

  get status(): string {
    return this.purchaseOrder?.status || 'draft';
  }

  get statusColor(): string {
    const statusColors: Record<string, string> = {
      draft: 'grey',
      draft_preview: 'indigo',  // Preview from AI Review
      sent: 'blue',
      confirmed: 'orange',
      partial_received: 'purple',
      received: 'green',
      canceled: 'red'
    };
    return statusColors[this.status] || 'grey';
  }

  get statusLabel(): string {
    return this.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  get supplierName(): string {
    return this.purchaseOrder?.supplierName || 'Unknown Supplier';
  }

  get supplierId(): number | null {
    return this.purchaseOrder?.supplierId || null;
  }

  get lineItems(): any[] {
    return this.purchaseOrder?.items || [];
  }

  get orderDate(): string {
    return this.purchaseOrder?.orderDate || '';
  }

  get expectedDeliveryDate(): string | null {
    return this.purchaseOrder?.expectedDeliveryDate || null;
  }

  get notes(): string | null {
    return this.purchaseOrder?.notes || null;
  }

  get locationId(): number | null {
    // Check draft PO data first (from action parameters), then model
    const draftPO = this.actionParameters?.draft_po;
    if (draftPO?.locationId) {
      return draftPO.locationId;
    }
    return this.purchaseOrder?.locationId || null;
  }

  get locationName(): string | null {
    // Check draft PO data first (from action parameters), then model
    const draftPO = this.actionParameters?.draft_po;
    if (draftPO?.locationName) {
      return draftPO.locationName;
    }
    return null;  // PurchaseOrderModel might not have locationName
  }

  get hasLocation(): boolean {
    return this.locationId !== null && this.locationId > 0;
  }

  get totalAmount(): string {
    return this.purchaseOrder?.totalAmount?.toString() || '0.00';
  }

  get createdAt(): string {
    return this.purchaseOrder?.createdAt || new Date().toISOString();
  }

  get hasError(): boolean {
    return this.error !== null;
  }

  async mounted() {
    if (this.hasDraftPOData) {
      // Draft PO data provided directly - no API fetch needed
      this.loadFromDraftPO();
    } else if (this.poIdentifier) {
      // Real PO - fetch from API
      await this.fetchPurchaseOrder();
    } else {
      this.error = 'No purchase order ID or number provided';
    }
  }

  /**
   * Watch action parameters for changes and refetch when they change
   */
  @Watch('actionParameters', { deep: true })
  private onActionParametersChange(): void {
    // Clear existing state
    this.purchaseOrder = null;
    this.error = null;

    if (this.hasDraftPOData) {
      // Draft PO data provided directly - no API fetch needed
      this.loadFromDraftPO();
    } else if (this.poIdentifier) {
      // Real PO - fetch from API
      this.fetchPurchaseOrder();
    } else {
      this.error = 'No purchase order ID or number provided';
    }
  }

  /**
   * Load PO data from draft_po action parameter (no API fetch needed).
   * Converts DraftPOData to PurchaseOrderModel format.
   */
  private loadFromDraftPO(): void {
    const draftPO = this.actionParameters?.draft_po;
    if (!draftPO) {
      this.error = 'Draft PO data not found';
      return;
    }

    // Convert draft_po items to the format expected by the template
    const items = (draftPO.items || []).map(item => ({
      id: null,
      variantId: item.variantId,
      productName: item.productName,
      variant: item.variantName,
      sku: item.sku,
      supplierSku: null,
      orderedQuantity: item.orderedQuantity,
      receivedQuantity: 0, // Draft has no received items
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice ?? (Number(item.orderedQuantity) * Number(item.unitPrice)),
      variantImage: item.variantImage || null,
      note: null,
    }));

    // Create a PurchaseOrderModel-compatible object
    // Cast through unknown since draft PO has subset of fields
    this.purchaseOrder = {
      id: draftPO.id,
      poNumber: draftPO.poNumber,
      status: draftPO.status,
      supplierId: draftPO.supplierId,
      supplierName: draftPO.supplierName,
      orderDate: draftPO.orderDate,
      expectedDeliveryDate: draftPO.expectedDeliveryDate,
      items: items,
      totalAmount: draftPO.totalAmount,
      notes: draftPO.notes,
      createdAt: draftPO.createdAt,
    } as unknown as PurchaseOrderModel;

    // Sync screen context for draft preview
    this.syncScreenContext();
  }

  private async fetchPurchaseOrder() {
    // Only reserve request ID when actually making a network call
    this.fetchRequestId++;
    const currentRequestId = this.fetchRequestId;

    this.loading = true;
    this.error = null;

    try {
      // Use single API endpoint that auto-detects ID vs PO number
      const result = await this.purchaseOrders.getByIdentifier(this.poIdentifier);

      // Ignore stale responses from older requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      this.purchaseOrder = result;

      // Sync screen context so agent knows what user is viewing
      this.syncScreenContext();
    } catch (err: any) {
      // Ignore errors from stale requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      this.error = err.message || 'Failed to load purchase order';

      EventBus.$emit('show-snackbar', {
        message: `Error loading purchase order: ${this.error}`,
        color: 'error'
      });
    } finally {
      // Only update loading state if this is still the current request
      if (currentRequestId === this.fetchRequestId) {
        this.loading = false;
      }
    }
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  }

  private formatDateShort(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  }

  /**
   * Navigate to full PO detail page (for real POs)
   */
  private viewOrderDetails() {
    // Use the actual PO ID from the loaded purchase order
    // This ensures we have the numeric ID even if we loaded by PO number
    const poId = this.purchaseOrder?.id?.toString() || this.poIdentifier;

    // Navigate to PO detail page
    this.$router.push({
      name: 'purchase-order-detail',
      params: { id: poId }
    });
  }

  /**
   * Confirm and finalize the draft PO (for draft preview).
   * If location is already set, creates PO directly. Otherwise shows location dialog.
   */
  private confirmAndCreate() {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar', {
        message: 'No purchase order data available',
        color: 'error'
      });
      return;
    }

    // If location is already set, create PO directly
    if (this.hasLocation) {
      this.createPOWithLocation({
        locationId: this.locationId!,
        locationName: this.locationName || ''
      });
    } else {
      // Show location dialog - actual PO creation happens in onLocationConfirmed
      this.showLocationDialog = true;
    }
  }

  /**
   * Handle location selection confirmation from dialog.
   */
  private onLocationConfirmed(selection: LocationSelection) {
    this.createPOWithLocation(selection);
  }

  /**
   * Handle location dialog cancellation.
   */
  private onLocationCancelled() {
    // User cancelled - no action needed
  }

  /**
   * Create PO directly via API with the specified delivery location.
   */
  private async createPOWithLocation(selection: LocationSelection) {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar', {
        message: 'No purchase order data available',
        color: 'error'
      });
      return;
    }

    if (!this.supplierId) {
      EventBus.$emit('show-snackbar', {
        message: 'Supplier information is missing. Please select a supplier.',
        color: 'error'
      });
      return;
    }

    const validatedSupplierId = this.supplierId;
    this.creating = true;

    try {
      // Convert line items to IPurchaseOrderItem format
      const items: IPurchaseOrderItem[] = this.lineItems.map(item => ({
        variantId: parseInt(item.variantId, 10) || 0,
        productName: item.productName || '',
        variant: item.variant || 'Default',
        sku: item.sku || '',
        supplierSku: item.supplierSku || '',
        variantImage: item.variantImage || '',
        orderedQuantity: item.orderedQuantity || 0,
        receivedQuantity: item.receivedQuantity || 0,
        unitPrice: item.unitPrice || 0,
        totalPrice: item.totalPrice || 0,
        note: item.note || ''
      }));

      // Create PO using PurchaseOrderModel with locationId
      const poData = {
        supplierId: validatedSupplierId,
        supplierName: this.supplierName,
        status: 'draft' as const,
        orderDate: this.orderDate || new Date().toISOString(),
        expectedDeliveryDate: this.expectedDeliveryDate || undefined,
        totalAmount: items.reduce((sum, item) => sum + item.totalPrice, 0),
        locationId: selection.locationId,
        notes: this.notes || undefined,
        items
      };

      const newPO = new PurchaseOrderModel(poData);
      await newPO.save();

      // Verify PO was saved successfully with an ID
      if (!newPO.id) {
        throw new Error('Purchase order was not saved correctly - missing ID');
      }

      // Update context with the created PO
      if (contextSyncService.getSessionId()) {
        await contextSyncService.updateSelectionContext(
          'purchase_order',
          [newPO.id],
          [{
            po_id: newPO.id,
            po_number: newPO.poNumber,
            total_amount: newPO.totalAmount,
            supplier_id: validatedSupplierId,
            supplier_name: this.supplierName,
            location_id: selection.locationId,
            location_name: selection.locationName
          }]
        );
      }

      // Emit event to display the created PO (ChatResponse format with actions array)
      EventBus.$emit('po-builder:preview', {
        actions: [{
          action: 'DISPLAY_PURCHASE_ORDER',
          parameters: {
            po_id: String(newPO.id),  // Use po_id for direct creation (not draft_po_id)
            po_number: newPO.poNumber
          }
        }],
        current_agent: 'Purchase Order Assistant'
      });

      EventBus.$emit('show-snackbar', {
        message: `Purchase order ${newPO.poNumber} created successfully`,
        color: 'success'
      });

    } catch (error) {
      console.error('[PurchaseOrderDisplay] Failed to create PO:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to create purchase order',
        color: 'error'
      });
    } finally {
      this.creating = false;
    }
  }

  /**
   * Sync current screen context to contextSyncService.
   * Called after PO is loaded to enable agent to understand what user is viewing.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId() || !this.purchaseOrder) {
      return;
    }

    contextSyncService.updateScreenContext({
      screen_name: 'purchase_order_detail',
      screen_path: '/purchase-assistant',
      screen_params: { po_id: this.poIdentifier },
      component_state: {
        screen_type: 'purchase_order_detail',
        screen_data: {
          po_id: this.purchaseOrder.id?.toString() || this.poIdentifier,
          po_number: this.poNumber,
          supplier_id: this.supplierId,
          supplier_name: this.supplierName,
          status: this.status,
          total_amount: parseFloat(this.totalAmount),
          line_items_count: this.lineItems.length,
          line_items: this.lineItems.map(item => ({
            variant_id: item.variantId,
            product_name: item.productName,
            sku: item.sku,
            variant: item.variant,
            ordered_quantity: item.orderedQuantity,
            unit_price: item.unitPrice,
            total_price: item.totalPrice,
          })),
          order_date: this.orderDate,
          expected_delivery_date: this.expectedDeliveryDate,
          notes: this.notes,
        },
      },
    });

    // Emit PO line items as discussed products so agent can reference them
    // This enables "create draft with same items" workflow
    this.emitDiscussedProducts();
  }

  /**
   * Emit PO line items as discussed products via EventBus.
   * This allows the agent to know what products the user is viewing/discussing
   * and enables workflows like "create a draft with the same items".
   */
  private emitDiscussedProducts(): void {
    if (!this.lineItems || this.lineItems.length === 0) {
      return;
    }

    // Group line items by product name (to handle multi-variant products)
    const productsByName: Record<string, any[]> = {};
    for (const item of this.lineItems) {
      const productName = item.productName || 'Unknown';
      if (!productsByName[productName]) {
        productsByName[productName] = [];
      }
      productsByName[productName].push(item);
    }

    // Convert to discussed_products format expected by backend
    // Each entry has product_name and variants array
    const discussedProducts = Object.entries(productsByName).map(([productName, variants]) => ({
      product_name: productName,
      variants: variants.map(v => ({
        variantId: String(v.variantId || ''),
        productName: v.productName || productName,
        variantName: v.variant || 'Default',
        sku: v.sku || '',
        unitPrice: v.unitPrice || 0,
        orderedQuantity: v.orderedQuantity || 0,
        currentInventory: 0,  // Not available from PO detail
        suggestedQty: v.orderedQuantity || 0,  // Use ordered qty as suggested
      })),
    }));

    // Emit event for AgentChatbox to pick up
    EventBus.$emit('po-builder:discussed-products-loaded', {
      products: discussedProducts,
      source: 'purchase_order_detail',
      po_id: this.purchaseOrder?.id,
      po_number: this.poNumber,
      supplier_id: this.supplierId,
      supplier_name: this.supplierName,
    });
  }

  private retryLoad() {
    if (!this.poIdentifier) {
      return;
    }
    this.fetchPurchaseOrder();
  }
}
